import { useCallback, useRef } from 'react'
import { ColumnManager } from '../../../../../utils/ColumnManager'
import { formatCode } from '../../../../../utils/formatCode'
import {
  normalizeBranchCode,
  validateBranchCodeLocal
} from '../../../../../utils/customerValidation'
import {
  createTableInputResolver,
  getKeyboardNavigationDirection
} from '../utils/keyboardNavigation'

const FIELD_NAME_MAP = {
  torioiki: '取置',
  basho: '場所',
  edaban: '枝番'
}

export function useHeaderFieldEditing(columnManager, distributionManager) {
  const controlKeyRefs = useRef(new Map())
  const handleHeaderFieldChange = useCallback(
    (customerCode, fieldName, value) => {
      const beforeValue = columnManager.getCustomerHeaderField(
        customerCode,
        fieldName
      )
      columnManager.setCustomerHeaderField(customerCode, fieldName, value)

      const displayCode = formatCode(customerCode)

      if (fieldName === 'edaban') {
        const { customerCode: code } = ColumnManager.parseColumnKey(customerCode)
        const usedIndexes = new Set(
          columnManager
            .getCustomerColumns()
            .filter((col) => col.customerCode !== customerCode)
            .map((col) => ColumnManager.parseColumnKey(col.customerCode))
            .filter(
              ({ customerCode: existingCode, branchCode }) =>
                String(existingCode) === String(code) &&
                String(branchCode) === String(value)
            )
            .map(({ index }) => index)
        )
        let nextIndex = 0
        while (usedIndexes.has(nextIndex)) nextIndex += 1

        const oldColumnKey = customerCode
        const newColumnKey = ColumnManager.buildColumnKey(
          code,
          value,
          nextIndex
        )

        const applyUpdate = () => {
          columnManager.setCustomerHeaderField(customerCode, fieldName, value)
          columnManager.updateCustomerCode(oldColumnKey, newColumnKey)
          // Branch editing is a rename of the currently visible column. Do not
          // infer an older source occurrence from metadata, because after
          // reopening and adding a new duplicate customer that can point back to
          // the first blank-branch column and move its quantities.
          distributionManager.updateCustomerCode(oldColumnKey, newColumnKey)
        }

        const revertUpdate = () => {
          columnManager.setCustomerHeaderField(
            newColumnKey,
            fieldName,
            beforeValue
          )
          columnManager.updateCustomerCode(newColumnKey, oldColumnKey)
          distributionManager.updateCustomerCode(newColumnKey, oldColumnKey)
        }

        applyUpdate()

        distributionManager.addLogEntry({
          action: `[${distributionManager.username}] ${displayCode} の ${FIELD_NAME_MAP[fieldName]} を「${value}」に変更しました`,
          up: applyUpdate,
          down: revertUpdate
        })
      } else {
        distributionManager.addLogEntry({
          action: `[${distributionManager.username}] ${displayCode} の ${FIELD_NAME_MAP[fieldName]} を「${value}」に変更しました`,
          up: () => {
            columnManager.setCustomerHeaderField(customerCode, fieldName, value)
          },
          down: () => {
            columnManager.setCustomerHeaderField(
              customerCode,
              fieldName,
              beforeValue
            )
          }
        })
      }
    },
    [columnManager, distributionManager]
  )

  const TORIOIKI_STATES = ['', '●', '＋1日', '＋2日']

  const handleHeaderFieldToggle = useCallback(
    (customerCode, fieldName) => {
      const currentValue = columnManager.getCustomerHeaderField(
        customerCode,
        fieldName
      )
      let nextValue
      if (fieldName === 'torioiki') {
        const currentIndex = TORIOIKI_STATES.indexOf(currentValue)
        const nextIndex = (currentIndex + 1) % TORIOIKI_STATES.length
        nextValue = TORIOIKI_STATES[nextIndex]
      } else {
        nextValue = currentValue === '●' ? '' : '●'
      }
      handleHeaderFieldChange(customerCode, fieldName, nextValue)
    },
    [columnManager, handleHeaderFieldChange]
  )

  const handleHeaderFieldBlur = useCallback(
    async (e, customerCode, fieldName) => {
      controlKeyRefs.current.delete(`${customerCode}::${fieldName}`)
      const rawValue = e.target.value.trim()
      const value =
        fieldName === 'edaban' ? normalizeBranchCode(rawValue) : rawValue
      if (fieldName === 'edaban' && value !== rawValue) {
        e.target.value = value
      }
      const currentValue = columnManager.getCustomerHeaderField(
        customerCode,
        fieldName
      )

      if (value !== currentValue) {
        if (fieldName === 'edaban') {
          const localError = validateBranchCodeLocal(value)
          if (localError) {
            const inputEl = e.target
            inputEl.value = currentValue
            alert(localError)
            setTimeout(() => {
              inputEl.focus()
              inputEl.select()
            }, 0)
            return false
          }
        }

        handleHeaderFieldChange(customerCode, fieldName, value)
      }
      return true
    },
    [columnManager, handleHeaderFieldChange]
  )

  const ensureVisible = (element) => {
    const scrollContainer = element.closest('.rp-table-scroll')
    if (!scrollContainer) return
    
    const rect = element.getBoundingClientRect()
    const containerRect = scrollContainer.getBoundingClientRect()
    
    const stickyCols = Array.from(scrollContainer.querySelectorAll('.sticky-col'))
    let stickyWidth = 0
    if (stickyCols.length > 0) {
      const lastSticky = stickyCols[stickyCols.length - 1]
      const lastStickyRect = lastSticky.getBoundingClientRect()
      stickyWidth = lastStickyRect.right - containerRect.left
    }

    if (rect.left < containerRect.left + stickyWidth) {
      scrollContainer.scrollLeft -= (containerRect.left + stickyWidth - rect.left + 20)
    } else if (rect.right > containerRect.right) {
      scrollContainer.scrollLeft += (rect.right - containerRect.right + 20)
    }
  }

  const findNextInput = (currentInput, direction) => {
    const currentTd = currentInput.closest('td, th')
    if (!currentTd) return null
    const currentTr = currentTd.closest('tr')
    if (!currentTr) return null
    
    const allTrs = Array.from(document.querySelectorAll('.rp-table tr'))
    const currentTrIdx = allTrs.indexOf(currentTr)

    const isAutocomplete = (el) => el.closest('.MuiAutocomplete-root')

    if (direction === 'up' || direction === 'down') {
      const step = direction === 'up' ? -1 : 1
      const currentRect = currentTd.getBoundingClientRect()
      const currentCenter = currentRect.left + currentRect.width / 2

      for (let i = currentTrIdx + step; i >= 0 && i < allTrs.length; i += step) {
        const row = allTrs[i]
        const cells = Array.from(row.cells)
        
        const targetTd = cells.find(td => {
           const rect = td.getBoundingClientRect()
           return currentCenter >= rect.left && currentCenter <= rect.right
        })

        if (targetTd) {
          const input = targetTd.querySelector('input')
          if (input && !input.disabled && !input.readOnly && !isAutocomplete(input)) {
            return input
          }
        }
      }
    } else if (direction === 'left' || direction === 'right') {
      const allInputs = Array.from(
        document.querySelectorAll('.rp-table input')
      ).filter(i => !i.disabled && !i.readOnly && !isAutocomplete(i))
      
      const currentIndex = allInputs.indexOf(currentInput)
      if (direction === 'left' && currentIndex > 0) return allInputs[currentIndex - 1]
      if (direction === 'right' && currentIndex < allInputs.length - 1) return allInputs[currentIndex + 1]
    }
    return null
  }

  const handleHeaderFieldKeyDown = useCallback(
    async (e, customerCode, fieldName) => {
      const { key, target } = e
      const controlKey = `${customerCode}::${fieldName}`
      const controlState = controlKeyRefs.current.get(controlKey) || {
        pending: false,
        combined: false
      }
      if (key === 'Control') {
        controlKeyRefs.current.set(controlKey, {
          pending: true,
          combined: false
        })
        return
      }
      if (e.ctrlKey || controlState.pending) {
        if (controlState.pending) controlState.combined = true
        controlKeyRefs.current.set(controlKey, controlState)
        return
      }
      const keyboardDirection = getKeyboardNavigationDirection(key)
      if (keyboardDirection) {
        e.preventDefault()
        const nextInput = findNextInput(target, keyboardDirection)
        const resolveNextInput =
          fieldName === 'edaban'
            ? createTableInputResolver(nextInput)
            : () => nextInput

        const success = await handleHeaderFieldBlur(e, customerCode, fieldName)
        if (!success) return

        if (nextInput) {
          setTimeout(() => {
            const currentNextInput = resolveNextInput()
            if (!currentNextInput) return
            currentNextInput.focus()
            currentNextInput.select()
            ensureVisible(currentNextInput)
          }, 0)
        }
      } else if (key === 'Escape') {
        e.preventDefault()
        const savedValue = columnManager.getCustomerHeaderField(
          customerCode,
          fieldName
        )
        target.value = savedValue
        target.blur()
      } else if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(key)) {
        const isLeft = key === 'ArrowLeft'
        const isRight = key === 'ArrowRight'
        const isUp = key === 'ArrowUp'
        const isDown = key === 'ArrowDown'

        if (isLeft && target.selectionStart !== 0) return
        if (isRight && target.selectionEnd !== target.value.length) return

        const direction = isUp ? 'up' : isDown ? 'down' : isLeft ? 'left' : 'right'
        const nextInput = findNextInput(target, direction)
        
        if (nextInput) {
          e.preventDefault()
          nextInput.focus()
          nextInput.select()
          ensureVisible(nextInput)
        }
      }
    },
    [columnManager, handleHeaderFieldBlur]
  )

  const handleHeaderFieldKeyUp = useCallback(
    async (e, customerCode, fieldName) => {
      if (e.key !== 'Control') return
      const controlKey = `${customerCode}::${fieldName}`
      const controlState = controlKeyRefs.current.get(controlKey)
      controlKeyRefs.current.delete(controlKey)
      if (!controlState?.pending || controlState.combined) return

      e.preventDefault()
      const nextInput = findNextInput(e.currentTarget, 'left')
      const resolveNextInput =
        fieldName === 'edaban'
          ? createTableInputResolver(nextInput)
          : () => nextInput
      const success = await handleHeaderFieldBlur(
        e,
        customerCode,
        fieldName
      )
      if (!success || !nextInput) return

      setTimeout(() => {
        const currentNextInput = resolveNextInput()
        if (!currentNextInput) return
        currentNextInput.focus()
        currentNextInput.select()
        ensureVisible(currentNextInput)
      }, 0)
    },
    [handleHeaderFieldBlur]
  )

  return {
    handleHeaderFieldBlur,
    handleHeaderFieldKeyDown,
    handleHeaderFieldKeyUp,
    handleHeaderFieldToggle
  }
}
